import { EnumEndpointsName } from "@/constants/app/type";
import { TypeCommonAPIState } from "../../type";
import type { GroupedDestination } from "@/utils/hotel/groupDestinationsByCity";

export type HotelFeeConfigUnitType = "FIXED" | "PERCENT"
export type HotelFeeConfigOperation = "ADD" | "SUBTRACT" | "MULTIPLY" | "DIVIDE"
export type HotelFeeCalculationMethod =
  | "PER_BOOKING"
  | "PER_ROOM"
  | "PER_ROOM_NIGHT"
  | "ORIGINAL_CANCELLATION_FEE"

export type HotelFeeConfig = {
  unitType: HotelFeeConfigUnitType
  amount: number
  operation: HotelFeeConfigOperation
  currency?: string
  calculationMethod: HotelFeeCalculationMethod
  refund?: boolean
}

export type HotelFeePolicy = {
  id: string
  name?: string
  clientId?: string
  priority: number
  status: "ACTIVE" | "INACTIVE" | "DRAFT"
  productType: "HOTEL" | "FLIGHT" | "PAYMENT_GATEWAY" | "ESIM"
  feeConfigs: HotelFeeConfig[] | HotelFeeConfig[][] | unknown
  conditions?: unknown[]
  tags?: string
  summary?: string
}

export type HotelMarkupContext = {
  roomsNum: number
  nights: number
}

export interface HotelState {
  searchingDestination?: boolean,
  formSearch: {
    location?: HotelLocation,
    currentLocation?: HotelLocation,
    searchType?: string,
    checkInDate?: string,
    checkOutDate?: string,
    roomsNum?: number,
    adults?: number,
    children?: number,
    childrenAges?: number[],
    page?: number,
    pageSize?: number,
    filter?: any,
    sort?: any,
    locale?: string,
    searchMode?: string,
    init?: boolean
  },
  firstHotel: Hotel | null,
  typeSearch: 'INTERNAL' | 'EXTERNAL',
  mode: 'maps' | 'list',
  modeDetail: 'maps' | 'list',
  firstLoadingHotels: boolean,
  vietnamPopularDestinations: HotelLocation[],
  internationalPopularDestinations: HotelLocation[],
  popularGroupsInternal: GroupedDestination[],
  popularGroupsExternal: GroupedDestination[],
  [EnumEndpointsName.HOTEL_GOOD_PRICES]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_MASTER_DETAIL]: TypeCommonAPIState<HotelMaster>,
  [EnumEndpointsName.HOTEL_AREAS]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_BOOKING_CANCEL]: TypeCommonAPIState<CancelHotelResponse>,
  [EnumEndpointsName.HOTEL_BOOKING_CONFIRM_CANCEL]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_CITIES]: TypeCommonAPIState<{ [cityId: number]: HotelCity }>,
  [EnumEndpointsName.HOTEL_COUNTRIES]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_DETAIL]: TypeCommonAPIState<HotelDetail> & { feePolicies: HotelFeePolicy[] },
  [EnumEndpointsName.HOTEL_DETAIL_STATISTICS]: TypeCommonAPIState<HotelDetailStatisticsResponse>,
  [EnumEndpointsName.HOTEL_DETAIL_REVIEWS]: TypeCommonAPIState<HotelDetailReviewsResponse>,
  [EnumEndpointsName.HOTEL_DETAIL_RESOURCE]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_FILTER_OPTION]: TypeCommonAPIState<{ [category: string]: HotelFilter }>,
  [EnumEndpointsName.HOTEL_HEALTHZ]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_LOCATIONS_INIT]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_LOCATIONS_SEARCH]: TypeCommonAPIState<{ [locationId: string]: HotelLocation }>,
  [EnumEndpointsName.HOTEL_LOCATIONS_DETAIL]: TypeCommonAPIState<HotelLocation>,
  [EnumEndpointsName.HOTEL_ORDER]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_ORDER_PRECHECK]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_SEARCH]: TypeCommonAPIState<{ [hotelId: number]: Hotel }> & {
    primaryKeysForMaps: any[]
    feePolicies: HotelFeePolicy[]
  },
  [EnumEndpointsName.HOTEL_SEARCH_KEYWORD]: TypeCommonAPIState<any>,
  [EnumEndpointsName.HOTEL_SORT_OPTION]: TypeCommonAPIState<any>,
}

export type CancelHotelResponse = {
  cancellationSummary?: {
    bookingId?: number,
    reference?: number,
    cancellationPolicy?: {
      language: string,
      policyText: string,
    }[],
    paymentRate?: {
      currency: string,
      inclusive: number
    }[],
    refundRate?: {
      currency: string,
      inclusive: number
    }[]
  },
  order?: any
}


export type HotelMaster = {
  id: string
  clientId: string
  name: string
  slug: string
  headline: string
  description: string
  star: number
  starRating: number
  address: string
  city: string
  area: string
  country: string
  highlightLocation: ({
    icon: string,
    text: string
  })[],
  cta: {
    url: string,
    text: string
  },
  lat: number
  lng: number
  mapEmbedUrl: string
  status: string
  source: string
  media: ({
    url: string,
    type: string,
    order: number,
    category: string
  })[],
  amenities: ({
    name: string,
    category: string
  })[],
  policies: ({
    name: string,
    content: string,
    category: string
  })[],
  poi: ({
    name: string,
    type: string,
    distance: string
  })[],
  serviceNote: string
  checkInTime: string
  checkOutTime: string
  ratingScore: number
  ratingCount: number
  reviews: ({
    score: number,
    images: string[],
    comment: string,
    userName: string
  })[],
  publishedAt: string
  createdAt: string
  updatedAt: string
  decimal: any,
  rooms: ({
    id: string
    clientId: string
    hotelId: string
    name: string
    images: string[],
    sizeM2: number,
    bedType: string
    maxGuest: number,
    view: string
    amenities: ({
      name: string,
      category: string
    })[],
    basePrice: number,
    priceSale: number,
    currency: string
    priceIncludedTax: boolean
    pricingNote: string
    cta: {
      url: string
      text: string
    },
    facilities: string[],
    services: string[],
    serviceNote: string
    surchargeNote: string
    checkInTime: string
    checkOutTime: string
    availableSlot: number
    createdAt: string
    updatedAt: string
  })[]
}

export type HotelDetailStatisticsResponse = {
  score: ({
    hotelId: number
    providerId: number
    isExternal: boolean,
    providerLogoUrl: string,
    providerName: string,
    averageScore: number
    totalReviews: number
    distribution: ({
      id: string,
      scoreText: string,
      count: number
    })[],
    grades: ({
      id: string,
      name: string,
      scoreText: string,
      value: number
    })[]
  })[]
}

export type HotelDetailReviewsResponse = {
  items: ({
    checkInDate: string,
    checkOutDate: string,
    hotelId: number
    providerId: number
    reviewId: number
    isExternal: boolean
    providerName: string
    reviewer: {
      countryName: string
      displayMemberName: string
      flagName: string
      reviewGroupName: string
      roomTypeName: string
      countryId: number
      lengthOfStay: number
      reviewGroupId: number
      roomTypeId: number
      reviewerReviewedCount: number
      isExpertReviewer: boolean
      isShowGlobalIcon: boolean
      isShowReviewedCount: boolean
    },
    images: any[],
    reviewDate: string
    rating: number
    languageCode: string
    comment: string
    title: string
    locationScore: number
    cleanlinessScore: number
    staffPerformanceScore: number
    facilitiesScore: number
    valueForMoneyScore: number
  })[],
  pagination: {
    page: number
    pageSize: number
    total: number
    totalPages: number
    hasNext: boolean,
    hasPrev: boolean
  }
}

export enum EnumHotelSearchType {
  CONTINENT = 'CONTINENT',
  COUNTRY = 'COUNTRY',
  CITY = 'CITY',
  AREA = 'AREA',
  HOTEL = 'HOTEL'
}

export type HotelLocation = {
  searchType?: string,
  text?: string,
  textTranslate?: string,
  textTranslateNormalized?: string,
  districtVariants?: string[],
  displayName?: string,
  slug?: string,
  activeHotel?: number,
  countryId?: number,
  cityId?: number,
  countryName?: string,
  countryNameTranslate?: string,
  id: number,
  longitude?: number,
  latitude?: number,
  nameLang?: {
    vi?: string,
    en?: string,
  },
  isDomestic?: boolean,
  isActive?: boolean,
  _id: string,
  cityName?: string,
  cityNameTranslate?: string,
  address?: {
    countryCode?: number,
    countryName?: string,
    cityName?: string,
    cityNameVi?: string,
    cityId?: number,
    address?: {
      addressLine1?: string,
      addressLine2?: string,
      postalCode?: number,
      stateEn?: string,
      stateVi?: string
    }
  },
  country?: number,
  type?: string[],
  isPopular?: number,
  imageViewUrl?: string
  /** Ảnh quốc gia (popular quốc tế) */
  countryImageViewUrl?: string
  /** Ảnh thành phố cha (popular nội địa — nhóm quận/huyện) */
  cityImageViewUrl?: string
  supplier?: string
  tagName?: string[],
  labelLang?: string,

  // ADDITIONAL FIELDS FOR CITY
  localeDisplayType: string,
  numsHotel: number,
}

export type HotelFilterItem<T> = {
  key: number,
  count?: number,
  text: string,
  textEn: string,
  value: T
}

export type HotelFilterRange = [number, number]
export type HotelFilterCheckbox<T> = HotelFilterItem<T>[]

export type HotelFilter = {
  category: string,
  type: string,
  options: HotelFilterRange | HotelFilterCheckbox<any>
}

export type HotelCity = {
  id: number,
  cityId: number,
  countryId: number,
  cityName: string,
  cityTranslated: string,
  activeHotels: number,
  longitude: string,
  latitude: string,
  noArea: number,
  pullingHotelAt: null,
  createdAt: string,
  updatedAt: string
}

export type HotelArea = {
  areaId?: number,
  areaName?: string,
  areaTranslated?: string,
}

export type Hotel = {
  id: number,
  hotelId: number,
  hotelName: string,
  hotelFormerlyName: string,
  translatedName: string,
  starRating: string,
  cityId: number,
  latitude: string,
  longitude: string,
  countryId: number,
  areaId: number,
  popularityScore: number,
  remark: string,
  numberOfReviews: number,
  ratingAverage: string,
  descriptions: string,
  accommodationType: string,
  pictures: {
    [name: string]: string[] // image[]
  },
  city: {
    id: number,
    cityId: number,
    countryId: number,
    cityName: string,
    cityTranslated: string,
    activeHotels: number,
    longitude: string,
    latitude: string,
    noArea: number,
    pullingHotelAt: any,
    createdAt: string,
    updatedAt: string
  },
  area?: HotelArea,
  distanceFromCenter: number,
  benefits: HotelBenefit[],
  remainingRooms: number,
  freeBreakfast: boolean,
  costPerNight: {
    currency: string,
    exclusive: number,
    inclusive: number,
    tax: number,
    fees: number
  },
  paymentModel: string,
  searchId: number,
  normalBedding: number,
  extraBeds: number,
  freeCancellation: boolean,
  basePrice: number
  promotionDetails?: HotelPromotion
}

export type HotelBenefit = {
  id: number,
  benefitName: string,
  translatedBenefitName: string
}

export type HotelPromotion = {
  promotionId: number,
  codeEligible: boolean,
  description: string,
  savingAmount: number
}

export type HotelAddress = {
  hotelId: number,
  addressType: 'English address' | 'Local language',
  addressLine_1: string,
  addressLine_2: number,
  postalCode: number,
  state: string,
  city: string,
  country: string
}

export type HotelFacility = {
  hotelId: number,
  propertyGroupDescription: string,
  propertyId: number,
  propertyName: string,
  propertyTranslatedName: string
}

export type HotelRoomType = {
  hotelId: number,
  hotelRoomTypeId: number,
  standardCaption: string,
  standardCaptionTranslated: string,
  maxOccupancyPerRoom: number,
  noOfRoom: number,
  sizeOfRoom: number,
  roomSizeInclTerrace: boolean,
  views: number,
  maxExtrabeds: number,
  maxInfantInRoom: number,
  hotelRoomTypePicture: string,
  bedType: number,
  hotelMasterRoomTypeId: number,
  hotelRoomTypeAlternateName: number,
  sharedBathroom: boolean,
  gender: number,
  hotelRoomTypePictures: string[]
}

export type HotelPicture = {
  hotelId: number,
  pictureId: number,
  caption: string,
  captionTranslated: string,
  URL: string
}

export type HotelTaxBreakdown = {
  id: string,
  typeValue: string,
  taxDescription: string,
  translatedTaxDescription: string,
  method: string,
  currency: string,
  base: string,
  taxable: string,
  percent: number,
  amount: number
}

export type HotelRoom = {
  roomId: number,
  blockId: string,
  roomName: string,
  parentRoomName: string,
  translatedRoomName: string,
  blockIdBackup: string,
  parentRoomId: number,
  ratePlanId: number,
  freeWifi: boolean,
  remainingRooms: number,
  normalBedding: number,
  extraBeds: number,
  freeBreakfast: boolean,
  freeCancellation: boolean,
  freeCancellationDate: string,
  payAtHotel?: boolean,
  totalPayment: {
    exclusive: number,
    inclusive: number,
    tax: number,
    fees: number,
    taxDueSupplier: number
  },
  promotionDetail?: {
    promotionId: number,
    codeEligible: boolean,
    description: string,
    savingAmount: number
  },
  roomTypeNotGuaranteed: boolean,
  paymentModel: string,
  rate: {
    currency: string,
    exclusive: number,
    inclusive: number,
    tax: number,
    fees: number,
    method: string
  },
  pictures: string[]
  perRoomPerNightRate: {
    currency: string,
    exclusive: number,
    inclusive: number,
    tax: number,
    fees: number
  },
  dailyRate: {
    date: string,
    exclusive: number,
    inclusive: number,
    tax: number,
    fees: number,
    method: string
  }[],
  surcharges: any[],
  taxBreakdown: HotelTaxBreakdown[],
  cancellationPolicy: {
    code: string,
    cancellationText: string,
    translatedCancellationText: string,
    parameter: {
      days: number,
      charge: string,
      value: number
    }[],
    date: {
      before: string,
      rate: {
        exclusive: number,
        inclusive: number,
        tax: number,
        fees: number
      }
    }[]
  },
  benefits: HotelBenefit[],
  resource?: HotelResource,

  index?: number
}

export type HotelDetail = {
  hotelId: number,
  hotelName: string,
  hotelFormerlyName: number,
  translatedName: string,
  starRating: number,
  continentId: number,
  countryId: number,
  cityId: number,
  areaId: number,
  longitude: number,
  latitude: number,
  hotelUrl: string,
  popularityScore: number,
  remark: string,
  numberOfReviews: number,
  ratingAverage: number,
  childAndExtraBedPolicy: {
    infantAge: number,
    childrenAgeFrom: number,
    childrenAgeTo: number,
    childrenStayFree: boolean,
    minGuestAge: number
  },
  accommodationType: string,
  accommodationTypeEn: string,
  nationalityRestrictions: number,
  singleRoomProperty: boolean,
  taxId: number,
  companyTraceabilityInfo: {
    companyTraceabilityFlag: boolean
  },
  addresses: HotelAddress[],
  descriptions: {
    hotelId: number,
    overview: string,
    snippet: string
  },
  facilities: {
    [groupName: string]: HotelFacility[]
  },
  pictures: HotelPicture[],
  roomtypes: HotelRoomType[],
  searchResponse: {
    propertyId: number,
    propertyName: string,
    translatedPropertyName: string,
    propertyUtcOffset: string,
    roomIds: any[],
    rooms: {
      [roomId: number]: HotelRoom[]
    }
  },
  localInfo: {
    canWalkings: {
      categoryName: string,
      IATACode: string,
      name: string,
      distance: number,
      distanceUnit: string
    }[],
    others: {
      categoryName: string,
      IATACode: string,
      name: string,
      distance: number,
      distanceUnit: string
    }[],
    nearbyProperties: {
      nearbyProperty: {
        categoryName: string,
        IATACode: string,
        name: string,
        distance: number,
        distanceUnit: string
      }[]
    },
    nearbyPlaces: {
      nearbyPlace: {
        name: string,
        distance: number,
        distanceUnit: string
      }[]
    },
    topPlaces: {
      topPlace: {
        name: string,
        distance: number,
        distanceUnit: string
      }[]
    }
  },
  searchId: number
}

export type HotelResourceFacility = {
  hotelId: number,
  hotelRoomTypeId: number,
  propertyId: number,
  propertyName: string,
  translatedName: string,
  propertyTranslatedName?: string
}

export type HotelResource = {
  hotelId: number,
  hotelRoomTypeId: number,
  standardCaption: string,
  standardCaptionTranslated: number,
  maxOccupancyPerRoom: number,
  noOfRoom: number,
  sizeOfRoom: number,
  roomSizeInclTerrace: boolean,
  views: number,
  maxExtrabeds: number,
  maxInfantInRoom: number,
  hotelRoomTypePicture: string,
  hotelRoomTypePictures: {
    hotelRoomTypePicture: string[]
  },
  bedType: string,
  hotelMasterRoomTypeId: number,
  hotelRoomTypeAlternateName: number,
  sharedBathroom: boolean,
  gender: number,
  roomTypeFacility: {
    [groupName: string]: {
      [facilityId: number]: HotelResourceFacility
    }
  }
}